import { useMemo, useState } from 'react'
import { PermissionButton } from '../components/Permissions.js'
import { useParams, useSearchParams } from 'react-router-dom'
import { useApi, useAsync } from '../lib/useApi.js'
import { ErrorBox, Empty, Loading, PageHeader, relative } from '../components/ui.js'
import { ViewNav } from '../components/ViewNav.js'
import {
  build,
  describe as phrase,
  needsCheck,
  needsUsing,
  suggestName,
  type FormState,
  type PolicyOperation,
  type PolicyRole,
  type Shape,
} from '../lib/policy-form.js'
import type { DbTable, Policy } from '../lib/api.js'

/**
 * A project's database: what is in it, who may read it, and a place to run
 * a query.
 *
 * The policy tab is the reason this page exists. Everything else here the
 * CLI already does well; writing a policy as JSON in a terminal is fine for
 * an agent and unreasonable for a person, and until now that was the only
 * way. So the editor offers the four shapes people actually want and leaves
 * a JSON escape hatch for the fifth.
 */

export default function Database() {
  const { id } = useParams<{ id: string }>()
  const api = useApi()
  const [params, setParams] = useSearchParams()
  const requested = params.get('view') ?? 'data'
  const tab = ['data', 'structure', 'policies', 'query'].includes(requested) ? requested : 'data'
  const [reloadKey, setReloadKey] = useState(0)

  const schema = useAsync(() => api.projectSchema(id!), [api, id])
  const policies = useAsync(() => api.projectPolicies(id!), [api, id, reloadKey])
  const tables = schema.data?.schema.tables ?? []
  const selected = tables.find((t) => t.name === params.get('table')) ?? tables[0]

  return (
    <>
      <PageHeader
        title="Database"
        description="Browse your data, inspect structure, and manage access."
        actions={
          <button className="btn" onClick={schema.reload}>
            Refresh tables
          </button>
        }
      />
      <ViewNav
        current={tab}
        items={[
          ['data', 'Data'],
          ['structure', 'Structure'],
          ['policies', 'Policies'],
          ['query', 'SQL editor'],
        ]}
      />
      {tab === 'query' ? (
        <Query projectId={id!} />
      ) : schema.loading ? (
        <Loading />
      ) : schema.error ? (
        <ErrorBox retry={schema.reload}>{schema.error}</ErrorBox>
      ) : !selected ? (
        <Tables tables={[]} />
      ) : (
        <div className="database-workspace">
          <aside className="table-sidebar" aria-label="Database tables">
            <h2>
              Tables <span className="sub small">{tables.length}</span>
            </h2>
            {tables.map((t) => (
              <button
                key={t.name}
                className={selected.name === t.name ? 'table-choice selected' : 'table-choice'}
                aria-pressed={selected.name === t.name}
                onClick={() => {
                  const next = new URLSearchParams(params)
                  next.set('table', t.name)
                  next.delete('page')
                  setParams(next)
                }}
              >
                {t.name}
              </button>
            ))}
          </aside>
          <div className="database-content">
            {tab === 'data' && <Rows key={selected.name} projectId={id!} table={selected.name} />}
            {tab === 'structure' && <Tables tables={[selected]} />}
            {tab === 'policies' && (
              <Policies
                key={selected.name}
                projectId={id!}
                tables={[selected]}
                policies={(policies.data?.policies ?? []).filter((p) => p.table === selected.name)}
                loading={policies.loading}
                error={policies.error}
                onChanged={() => setReloadKey((n) => n + 1)}
              />
            )}
          </div>
        </div>
      )}
    </>
  )
}

function Rows({ projectId, table }: { projectId: string; table: string }) {
  const api = useApi()
  const [page, setPage] = useState(0)
  const rows = useAsync(
    () => api.projectRows(projectId, table, `?limit=50&offset=${page * 50}`),
    [api, projectId, table, page],
  )
  return (
    <section>
      <h2>{table}</h2>
      <p className="sub small">
        Read-only preview with service access. Row policies do not apply. Pages can shift as data changes.
      </p>
      {rows.loading ? (
        <Loading />
      ) : rows.error ? (
        <ErrorBox retry={rows.reload}>{rows.error}</ErrorBox>
      ) : (
        <>
          <ResultTable rows={rows.data?.rows ?? []} />
          <div className="pagination">
            <button className="btn" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </button>
            <span className="sub small">
              Page {page + 1} · {rows.data?.rows.length ?? 0} rows shown
            </span>
            <button
              className="btn"
              disabled={(rows.data?.rows.length ?? 0) < 50}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </section>
  )
}

function ResultTable({ rows }: { rows: Record<string, unknown>[] }) {
  if (!rows.length) return <Empty>No rows returned.</Empty>
  const columns = Object.keys(rows[0]!)
  return (
    <div className="card table-scroll" tabIndex={0} aria-label="Query results">
      <table className="grid">
        <thead>
          <tr>
            {columns.map((c) => (
              <th scope="col" key={c}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c} className="mono">
                  {row[c] == null
                    ? 'NULL'
                    : typeof row[c] === 'object'
                      ? JSON.stringify(row[c])
                      : String(row[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Tables({ tables }: { tables: DbTable[] }) {
  if (tables.length === 0) {
    return (
      <>
        <Empty>No tables yet.</Empty>
        <p className="sub small">
          Design a schema and apply it with <code>nimbly db push</code>, or ask an agent to.
        </p>
      </>
    )
  }
  return (
    <>
      {tables.map((t) => (
        <section key={t.name} style={{ marginBottom: 20 }}>
          <h2>{t.name}</h2>
          <div className="card">
            {t.columns.map((c) => (
              <div className="row" key={c.name}>
                <div className="row-main">
                  <div className="row-title mono">{c.name}</div>
                  <div className="row-sub">
                    {c.type}
                    {c.primaryKey ? ' · primary key' : ''}
                    {c.notNull ? ' · not null' : ''}
                    {c.unique ? ' · unique' : ''}
                    {c.references ? ` · → ${c.references.table}.${c.references.column}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  )
}

function Policies({
  projectId,
  tables,
  policies,
  loading,
  error,
  onChanged,
}: {
  projectId: string
  tables: DbTable[]
  policies: Policy[]
  loading: boolean
  error: string | null
  onChanged: () => void
}) {
  const api = useApi()
  const [busy, setBusy] = useState<number | null>(null)
  const [mutationError, setMutationError] = useState<string | null>(null)

  // Which tables nobody can read. The commonest reason an app appears
  // broken, and invisible unless it is said out loud.
  const openToAnon = useMemo(
    () => new Set(policies.filter((p) => p.role === 'anon' && p.operation === 'select').map((p) => p.table)),
    [policies],
  )
  const closed = tables.map((t) => t.name).filter((n) => !openToAnon.has(n))
  if (loading) return <Loading />
  if (error) return <ErrorBox retry={onChanged}>{error}</ErrorBox>

  return (
    <>
      <p className="sub" style={{ marginBottom: 16 }}>
        A table with no policy is closed. The key that ships in your app — the anon key — can do nothing until
        one says otherwise.
      </p>

      {closed.length > 0 && (
        <div className="notice">
          <strong>Closed to your app’s anon key: {closed.join(', ')}</strong>
          <p className="sub small" style={{ margin: '6px 0 0' }}>
            Deliberate for anything private. For anything the app should read, add a select policy.
          </p>
        </div>
      )}

      <PolicyEditor projectId={projectId} tables={tables} onCreated={onChanged} />

      <h2>In force</h2>
      {mutationError && <ErrorBox>{mutationError}</ErrorBox>}
      {error && <ErrorBox>{error}</ErrorBox>}
      <div className="card">
        {loading ? (
          <Loading />
        ) : policies.length === 0 ? (
          <Empty>No policies. Every table is closed.</Empty>
        ) : (
          policies.map((p) => (
            <div className="row" key={p.id}>
              <div className="row-main">
                <div className="row-title">
                  {p.table} — {phrase(p)}
                </div>
                <div className="row-sub">
                  <code>{p.name}</code> · created {relative(p.createdAt)}
                </div>
              </div>
              <PermissionButton
                permission="apikey:create"
                className="btn btn-danger"
                disabled={busy !== null}
                onClick={async () => {
                  if (!confirm(`Remove "${p.name}"? Anything it allowed stops being allowed.`)) return
                  setBusy(p.id)
                  setMutationError(null)
                  try {
                    await api.deleteProjectPolicy(projectId, p.id)
                    onChanged()
                  } catch (err) {
                    setMutationError(err instanceof Error ? err.message : 'Could not remove policy.')
                  } finally {
                    setBusy(null)
                  }
                }}
              >
                {busy === p.id ? 'Removing…' : 'Remove'}
              </PermissionButton>
            </div>
          ))
        )}
      </div>
    </>
  )
}

const SHAPES: { kind: Shape['kind']; label: string; needsColumn: boolean; needsValue?: boolean }[] = [
  { kind: 'everyone', label: 'Every row', needsColumn: false },
  { kind: 'column-true', label: 'Rows where a column is true', needsColumn: true },
  { kind: 'column-equals', label: 'Rows where a column equals…', needsColumn: true, needsValue: true },
  { kind: 'owned-by-user', label: 'Rows belonging to the signed-in user', needsColumn: true },
  { kind: 'custom', label: 'Write the expression myself', needsColumn: false },
]

function PolicyEditor({
  projectId,
  tables,
  onCreated,
}: {
  projectId: string
  tables: DbTable[]
  onCreated: () => void
}) {
  const api = useApi()
  const [tableName, setTableName] = useState(tables[0]?.name ?? '')
  const [role, setRole] = useState<PolicyRole>('anon')
  const [operation, setOperation] = useState<PolicyOperation>('select')
  const [kind, setKind] = useState<Shape['kind']>('everyone')
  const [column, setColumn] = useState('')
  const [value, setValue] = useState('')
  const [jsonText, setJsonText] = useState('true')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const columns = tables.find((t) => t.name === tableName)?.columns ?? []
  const spec = SHAPES.find((s) => s.kind === kind)!
  const shape: Shape =
    kind === 'custom'
      ? { kind, json: jsonText }
      : kind === 'column-equals'
        ? { kind, column, value }
        : kind === 'everyone'
          ? { kind }
          : { kind, column }

  const form: FormState = {
    table: tableName,
    role,
    operation,
    name: name || suggestName(role, operation),
    shape,
  }
  const preview = build(form)

  if (tables.length === 0) return null

  return (
    <form
      className="card"
      style={{ padding: 16, marginBottom: 16 }}
      onSubmit={async (e) => {
        e.preventDefault()
        const built = build(form)
        if (!built.ok) {
          setError(built.error)
          return
        }
        setBusy(true)
        setError(null)
        try {
          await api.putProjectPolicy(projectId, built.policy)
          onCreated()
          setName('')
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Could not save that policy.')
        } finally {
          setBusy(false)
        }
      }}
    >
      <strong>Create policy</strong>
      <div className="form-row" style={{ marginTop: 10 }}>
        <div className="field" style={{ flex: 1, marginBottom: 0 }}>
          <label htmlFor="p-table">Table</label>
          <select
            id="p-table"
            value={tableName}
            onChange={(e) => {
              setTableName(e.target.value)
              setColumn('')
            }}
          >
            {tables.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ width: 170, marginBottom: 0 }}>
          <label htmlFor="p-role">Role</label>
          <select id="p-role" value={role} onChange={(e) => setRole(e.target.value as PolicyRole)}>
            <option value="anon">anon (your app)</option>
            <option value="authenticated">authenticated</option>
          </select>
        </div>
        <div className="field" style={{ width: 140, marginBottom: 0 }}>
          <label htmlFor="p-op">May</label>
          <select
            id="p-op"
            value={operation}
            onChange={(e) => setOperation(e.target.value as PolicyOperation)}
          >
            {(['select', 'insert', 'update', 'delete'] as const).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row" style={{ marginTop: 10 }}>
        <div className="field" style={{ flex: 1, marginBottom: 0 }}>
          <label htmlFor="p-shape">Which rows</label>
          <select id="p-shape" value={kind} onChange={(e) => setKind(e.target.value as Shape['kind'])}>
            {SHAPES.map((s) => (
              <option key={s.kind} value={s.kind}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        {spec.needsColumn && (
          <div className="field" style={{ width: 190, marginBottom: 0 }}>
            <label htmlFor="p-col">Column</label>
            <select id="p-col" value={column} onChange={(e) => setColumn(e.target.value)}>
              <option value="">choose…</option>
              {columns.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {spec.needsValue && (
          <div className="field" style={{ width: 160, marginBottom: 0 }}>
            <label htmlFor="p-val">Equals</label>
            <input id="p-val" value={value} onChange={(e) => setValue(e.target.value)} placeholder="true" />
          </div>
        )}
      </div>

      {kind === 'custom' && (
        <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
          <label htmlFor="p-json">Expression</label>
          <input
            id="p-json"
            className="mono"
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            placeholder='{"col":"published","eq":true}'
          />
        </div>
      )}

      <div className="form-row" style={{ marginTop: 10 }}>
        <div className="field" style={{ flex: 1, marginBottom: 0 }}>
          <label htmlFor="p-name">Name</label>
          <input
            id="p-name"
            value={name}
            maxLength={64}
            placeholder={suggestName(role, operation)}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <PermissionButton permission="apikey:create" className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Create policy'}
        </PermissionButton>
      </div>

      {error && (
        <div style={{ marginTop: 10 }}>
          <ErrorBox>{error}</ErrorBox>
        </div>
      )}

      {/* What it will mean, in the same words the list uses - so nobody
          learns the effect of a policy only after saving it. */}
      <p className="sub small" style={{ marginTop: 10, marginBottom: 0 }}>
        {preview.ok ? (
          <>
            This will allow:{' '}
            <strong>
              {phrase({
                role,
                operation,
                using: needsUsing(operation) ? (preview.policy.using as unknown) : null,
                check: needsCheck(operation) ? (preview.policy.check as unknown) : null,
              })}
            </strong>{' '}
            on {tableName}.
          </>
        ) : (
          <span style={{ color: 'var(--amber)' }}>{preview.error}</span>
        )}
      </p>
    </form>
  )
}

function Query({ projectId }: { projectId: string }) {
  const api = useApi()
  const [sql, setSql] = useState('select 1 as ok')
  const [result, setResult] = useState<{ rows: Record<string, unknown>[]; note: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [page, setPage] = useState(0)

  return (
    <>
      <p className="sub" style={{ marginBottom: 12 }}>
        Runs with a service key, so <strong>policies do not apply here</strong>. To see what your app sees,
        use <code>nimbly rows &lt;table&gt; --as anon</code>.
      </p>
      <form
        className="card"
        style={{ padding: 16, marginBottom: 16 }}
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy) return
          setBusy(true)
          setError(null)
          setResult(null)
          setPage(0)
          try {
            const r = await api.projectSql(projectId, sql)
            setResult({
              rows: r.results ?? [],
              note: `${r.results?.length ?? 0} row(s) · ${r.meta?.changes ?? 0} changed · ${r.meta?.durationMs ?? '?'}ms`,
            })
          } catch (err) {
            setError(err instanceof Error ? err.message : 'That query failed.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="field" style={{ marginBottom: 10 }}>
          <label htmlFor="q-sql">SQL — SQLite dialect</label>
          <textarea
            id="q-sql"
            className="mono sql-editor"
            rows={9}
            spellCheck={false}
            value={sql}
            onChange={(e) => setSql(e.target.value)}
          />
        </div>
        <PermissionButton
          permission="apikey:create"
          className="btn btn-primary"
          disabled={busy || !sql.trim()}
        >
          {busy ? 'Running…' : 'Run'}
        </PermissionButton>
      </form>

      {error && <ErrorBox>{error}</ErrorBox>}
      {result && (
        <>
          <div className="card">
            {result.rows.length === 0 ? (
              <Empty>No rows returned.</Empty>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="grid">
                  <thead>
                    <tr>
                      {Object.keys(result.rows[0]!).map((c) => (
                        <th key={c}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.slice(page * 100, (page + 1) * 100).map((row, i) => (
                      <tr key={i}>
                        {Object.keys(result.rows[0]!).map((c) => (
                          <td key={c} className="mono">
                            {row[c] === null ? 'NULL' : String(row[c])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <p className="sub small">{result.note}</p>
          {result.rows.length > 100 && (
            <div className="pagination">
              <button className="btn" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </button>
              <span>
                Rows {page * 100 + 1}–{Math.min((page + 1) * 100, result.rows.length)} of {result.rows.length}{' '}
                returned
              </span>
              <button
                className="btn"
                disabled={(page + 1) * 100 >= result.rows.length}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </>
  )
}
